import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import intlTelInput from 'intl-tel-input';
import parsePhoneNumber, {
  CountryCode,
  parsePhoneNumberFromString
} from 'libphonenumber-js';

import { filter, finalize, tap } from 'rxjs';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { ProfileService } from '~app/app/~shared/services/profile.service';
import { SnackbarService } from '~app/app/~shared/services/snackbar.service';
import { phoneNumberValidator } from '~app/app/~shared/validators/phone-number.validator';
import { AuthFacade } from '~app/app/~store/facades/auth.facade';
import { GlobalFacade } from '~app/app/~store/facades/global.facade';
import { User } from '~core/models/auth.models';

@Component({
  selector: 'personal-information-form',
  templateUrl: './personal-information-form.component.html',
  styleUrl: './personal-information-form.component.scss'
})
export class PersonalInformationFormComponent
  extends BaseComponent
  implements OnDestroy, AfterViewInit
{
  snackbarService = inject(SnackbarService);
  authFacade = inject(AuthFacade);
  globalFacade = inject(GlobalFacade);
  profileService = inject(ProfileService);

  @ViewChild('phoneNumberInput') intlInputPhone?: ElementRef;

  defaultCountry = 'CI';
  isLoading = signal(false);
  isUserProfileLoaded = signal(false);

  phoneNumberIntlPluginRef?: intlTelInput.Plugin;
  parsedPhoneNumber?: string;
  currentUserProfile?: User;

  form = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    username: new FormControl('', [
      Validators.required,
      Validators.minLength(3)
    ]),
    phoneNumber: new FormControl('', [phoneNumberValidator('CI')]),
    email: new FormControl('', [Validators.required, Validators.email])
  });

  currentUser$ = this.authFacade.currentUser$.pipe(
    filter(Boolean),
    tap(currentUser => {
      this.currentUserProfile = currentUser;
      const phoneNumber = currentUser.phoneNumber
        ? parsePhoneNumber(currentUser.phoneNumber, 'CI')?.number
        : '';

      this.form.patchValue({
        email: currentUser.email,
        username: currentUser.username,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        phoneNumber
      });
      this.form.get('email')?.disable();
    })
  );

  violations$ = this.globalFacade.formViolations$.pipe(
    takeUntilDestroyed(this._destroyRef),
    filter(Boolean),
    tap(violations => {
      this.form.setErrors({});
      violations.forEach(violation =>
        this.form.get(violation.propertyPath)?.setErrors({
          httpError: violation.title
        })
      );
    })
  );

  ngAfterViewInit(): void {
    this.initPhoneNumberInputs();
  }

  onSubmit(): void {
    const phoneNumber = this.form.value.phoneNumber || '';

    if (!phoneNumber) return;

    const parsedPhoneNumber = parsePhoneNumber(phoneNumber, 'CI');

    const payload = {
      firstName: this.form.value.firstName! || '',
      lastName: this.form.value.lastName! || '',
      phoneNumber: `${parsedPhoneNumber?.countryCallingCode}${parsedPhoneNumber?.nationalNumber}`
    };

    this.isLoading.set(true);

    if (!this.currentUserProfile?.id) return;

    this.profileService
      .update(payload)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this.snackbarService.open({
              text: `Votre profil a été mis à jour.`,
              type: 'success'
            });
            this.form.markAsPristine();
          },
          error: error => this.globalFacade.handleFormViolations(error)
        }),
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .subscribe();
  }

  get formControls(): {
    username: FormControl<string | null>;
    firstName: FormControl<string | null>;
    lastName: FormControl<string | null>;
    phoneNumber: FormControl<string | null>;
  } {
    return this.form.controls;
  }

  initPhoneNumberInputs(): void {
    if (this.intlInputPhone?.nativeElement) {
      this.phoneNumberIntlPluginRef = intlTelInput(
        this.intlInputPhone.nativeElement,
        {
          allowDropdown: true,
          formatOnDisplay: true,
          autoInsertDialCode: true,
          nationalMode: false,
          initialCountry: this.defaultCountry,
          separateDialCode: true,
          onlyCountries: [this.defaultCountry],
          customContainer: 'country',
          dropdownContainer: document.body
        }
      );
    }
  }

  onPhoneNumberChange(): void {
    this.form
      .get('phoneNumber')
      ?.valueChanges.pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(Boolean),
        tap(res => {
          const selectedCountryData =
            this.phoneNumberIntlPluginRef!.getSelectedCountryData();
          const isoCode = selectedCountryData.iso2;
          const phoneNumber = parsePhoneNumberFromString(
            res!,
            isoCode.toUpperCase() as CountryCode
          );
          if (phoneNumber?.nationalNumber) {
            this.parsedPhoneNumber = '+' + selectedCountryData.dialCode + res;
            this.form.get('phoneNumber')?.setErrors(null);
          } else {
            this.form.get('phoneNumber')?.setErrors({
              phoneNumberError: 'Numéro de téléphone invalide'
            });
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (
      this.phoneNumberIntlPluginRef &&
      typeof this.phoneNumberIntlPluginRef.destroy === 'function'
    ) {
      this.phoneNumberIntlPluginRef.destroy();
    }
  }
}
